import { createContext, type FC, type ReactNode, useCallback, useState } from 'react';
import type { Treatment } from '@/model/Treatment.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { type DraftFormData, type GroupsTreatments } from '@/model/Draft.model';
import type { ID } from '@/model/Common.model';
import { type PlannedAnimal, type PlannedAnimalData } from '@/model/PlannedAnimal.model.ts';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { AnimalSexMap, type AnimalSex } from '@/model/Animal.model';
import { type MetadataFieldWithValue } from '@/model/Metadata.model.ts';

export type changeStepCommandType =
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; step: number };

export type saveAndAdvanceCommandType = { type: 'INITIATE' } | { type: 'COMPLETE' };

export type updateStudyGroupsCommandType =
  | { type: 'UPDATE_GROUPS'; groups: Array<TreatmentGroup> }
  | { type: 'UPDATE_TREATMENTS'; treatments: Array<Treatment> }
  | { type: 'UPDATE_GROUPS_TREATMENTS'; groupsTreatments: Array<GroupsTreatments> }
  | {
      type: 'UPDATE_ALL';
      data: { groups: Array<TreatmentGroup>; treatments: Array<Treatment>; groupsTreatments: Array<GroupsTreatments> };
    };

interface StudyCreateContextValue {
  currentStep: number | undefined;
  changeStep: (payload: changeStepCommandType) => void;
  stepReady: boolean;
  setStepReady: (ready: boolean) => void;
  savingDraft: boolean;
  setSavingDraft: (saveDraft: boolean) => void;
  advancingStep: boolean;
  saveAndAdvance: (payload: saveAndAdvanceCommandType) => void;
  groups: Array<TreatmentGroup>;
  treatments: Array<Treatment>;
  groupsTreatments: Array<GroupsTreatments>;
  plannedAnimalData: PlannedAnimalData;
  updateStudyGroups: (command: updateStudyGroupsCommandType) => void;
  setPlannedAnimalData: (plannedAnimalData: PlannedAnimalData) => void;
  loadDraftData: (formData: any) => void;
  draftId: ID | null;
  setDraftId: (draftId: ID | null) => void;
}

export const StudyCreateContext = createContext<StudyCreateContextValue>({
  currentStep: undefined,
  changeStep: () => {},
  stepReady: false,
  setStepReady: () => {},
  savingDraft: false,
  setSavingDraft: () => {},
  advancingStep: false,
  saveAndAdvance: () => {},
  groups: [],
  treatments: [],
  groupsTreatments: [],
  plannedAnimalData: { type: 'planned', animals: [] },
  updateStudyGroups: () => {},
  setPlannedAnimalData: () => {},
  loadDraftData: () => {},
  draftId: null,
  setDraftId: () => {},
});

interface StudyCreateProviderProps {
  children: ReactNode;
  initialDraftId?: ID | null;
}

export const StudyCreateProvider: FC<StudyCreateProviderProps> = ({ children, initialDraftId = null }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [advancingStep, setAdvancingStep] = useState<boolean>(false);
  const [stepReady, setStepReady] = useState<boolean>(false);
  const [groups, setGroups] = useState<Array<TreatmentGroup>>([]);
  const [treatments, setTreatments] = useState<Array<Treatment>>([]);
  const [groupsTreatments, setGroupsTreatments] = useState<Array<GroupsTreatments>>([]);
  const [plannedAnimalData, setPlannedAnimalData] = useState<PlannedAnimalData>({ type: 'planned', animals: [] });
  const [draftId, setDraftId] = useState<ID | null>(initialDraftId);

  const updateStudyGroups = useCallback((command: updateStudyGroupsCommandType) => {
    switch (command.type) {
      case 'UPDATE_GROUPS':
        setGroups(command.groups);
        break;
      case 'UPDATE_TREATMENTS':
        setTreatments(command.treatments);
        break;
      case 'UPDATE_GROUPS_TREATMENTS':
        setGroupsTreatments(command.groupsTreatments);
        break;
      case 'UPDATE_ALL':
        setGroups(command.data.groups);
        setTreatments(command.data.treatments);
        setGroupsTreatments(command.data.groupsTreatments);
        break;
    }
  }, []);

  const loadDraftData = useCallback(
    (formData: DraftFormData) => {
      if (_notNil(formData) && formData !== undefined) {
        updateStudyGroups({
          type: 'UPDATE_ALL',
          data: {
            groups: _notNil(formData.groups) && formData.groups !== undefined ? formData.groups : [],
            treatments: _notNil(formData.treatments) && formData.treatments !== undefined ? formData.treatments : [],
            groupsTreatments:
              _notNil(formData.groups_treatments) && formData.groups_treatments !== undefined
                ? formData.groups_treatments
                : [],
          },
        });

        if (_notNil(formData.animals) && formData.animals !== undefined) {
          const animalType = formData.animals.type;
          const draftAnimals =
            animalType === 'planned'
              ? formData.animals.planned_animals
              : animalType === 'registered'
                ? formData.animals.registered_animals
                : null;

          if (_notNil(draftAnimals) && draftAnimals !== undefined) {
            const plannedAnimals: Array<PlannedAnimal> = draftAnimals.map((animal: any) => ({
              temp_id: _notNil(animal.api_id) && _isNotBlank(animal.api_id) ? animal.api_id : animal.temp_id,
              name: animal.name,
              group_temp_id: animal.group_temp_id,
              dob: animal.dob,
              sex: AnimalSexMap[animal.sex as AnimalSex] ?? animal.sex,
              strain: '',
              species: '',
              strainApiId: animal.strain_api_id,
              metadata: _notNil(animal.metadata) && animal.metadata !== undefined ? animal.metadata : {},
            }));
            const updatedPlannedAnimals = plannedAnimals.map((animal) => {
              const updatedMetadata: Record<string, MetadataFieldWithValue> = {};
              if (_notNil(animal.metadata) && animal.metadata !== undefined) {
                Object.entries(animal.metadata).forEach(([id, metadataValue]: [string, any]) => {
                  const glossaryId = metadataValue.glossary_id;
                  if (_notNil(glossaryId) && _isNotEmpty(glossaryId)) {
                    updatedMetadata[glossaryId] = {
                      title:
                        _notNil(metadataValue.title) &&
                        _isNotBlank(metadataValue.title) &&
                        _isNotEmpty(metadataValue.title)
                          ? metadataValue.title
                          : id,
                      value:
                        (_notNil(metadataValue.value) &&
                          _isNotBlank(metadataValue.value) &&
                          _isNotEmpty(metadataValue.value)) ||
                        Array.isArray(metadataValue.value)
                          ? metadataValue.value
                          : '',
                      glossary_id: glossaryId,
                      field_type: metadataValue.field_type,
                    } as MetadataFieldWithValue;
                  }
                });
              }
              return {
                ...animal,
                metadata: updatedMetadata,
              };
            });

            if (animalType === 'planned') {
              setPlannedAnimalData({
                type: 'planned',
                animals: updatedPlannedAnimals,
              });
            } else {
              setPlannedAnimalData({
                type: 'registered',
                animals: updatedPlannedAnimals,
                animalsPerCage:
                  _notNil(formData.animals.cage_animal_count) && formData.animals.cage_animal_count !== 0
                    ? formData.animals.cage_animal_count
                    : 5,
              });
            }
          } else {
            if (animalType === 'skip') {
              setPlannedAnimalData({ type: 'skip' });
            }
          }
        }
      }
    },
    [updateStudyGroups]
  );

  const changeStep = useCallback((payload: changeStepCommandType) => {
    switch (payload.type) {
      case 'NEXT_STEP':
        setStepReady(false);
        setCurrentStep((prev) => (prev !== undefined ? prev + 1 : 0));
        break;
      case 'PREV_STEP':
        setStepReady(false);
        setCurrentStep((prev) => (prev !== undefined ? Math.max(prev - 1, 0) : 0));
        break;
      case 'GO_TO_STEP':
        setStepReady(false);
        setCurrentStep(payload.step);
    }
  }, []);

  const saveAndAdvance = useCallback(
    (payload: saveAndAdvanceCommandType) => {
      switch (payload.type) {
        case 'INITIATE':
          setStepReady(false);
          setSavingDraft(true);
          setAdvancingStep(true);
          break;
        case 'COMPLETE':
          setSavingDraft(false);
          setAdvancingStep(false);
          changeStep({ type: 'NEXT_STEP' });
      }
    },
    [changeStep]
  );

  const contextValue: StudyCreateContextValue = {
    currentStep,
    changeStep,
    stepReady,
    setStepReady,
    savingDraft,
    setSavingDraft,
    advancingStep,
    saveAndAdvance,
    groups,
    treatments,
    groupsTreatments,
    plannedAnimalData,
    updateStudyGroups,
    setPlannedAnimalData,
    loadDraftData,
    draftId,
    setDraftId,
  };

  return <StudyCreateContext.Provider value={contextValue}>{children}</StudyCreateContext.Provider>;
};
